import { useCallback, useEffect, useState } from "react";
import { AIDE_HTTP_BASE, AIDE_HTTP_PATH } from "./env";

// Shape mirrors lifestyle_api/alexandria_runtime.py + routes/alexandria.py.

export interface CardRating {
  value: number;
  count: number;
}

export interface CardAction {
  id: string;
  label: string;
}

export interface AlexandriaCard {
  id: string;
  title: string;
  subtitle: string;
  vertical: string;
  card_type: string;
  image_url: string | null;
  source_url: string;
  source: string;
  geography_label: string;
  price_band: string | null;
  rating: CardRating | null;
  actions: CardAction[];
}

export interface AlexandriaSection {
  id: string;
  title: string;
  blurb: string;
  cards: AlexandriaCard[];
}

export interface AlexandriaSectionsResponse {
  consumer_id: string;
  sections: AlexandriaSection[];
  total_crawled_records: number;
}


export type AlexandriaFetchStatus = "idle" | "loading" | "ok" | "error";


export function useAlexandriaSections(consumerId: string): {
  status: AlexandriaFetchStatus;
  data: AlexandriaSectionsResponse | null;
  error: string | null;
  refresh: () => void;
} {
  const [status, setStatus] = useState<AlexandriaFetchStatus>("idle");
  const [data, setData] = useState<AlexandriaSectionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSections = useCallback(async () => {
    if (!consumerId) return;
    setStatus("loading");
    setError(null);
    try {
      // AIDE_HTTP_PATH carries the /app/lifestyle prefix when behind
      // CloudFront, so /alexandria sections route through the same
      // edge that handles aide HTTP — drop /v1/aide, append /v1/alexandria.
      const alexandriaPath = AIDE_HTTP_PATH.replace(/\/v1\/aide$/, "/v1/alexandria");
      const url = `${AIDE_HTTP_BASE}${alexandriaPath}/sections?consumer_id=${encodeURIComponent(consumerId)}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const body: AlexandriaSectionsResponse = await resp.json();
      setData(body);
      setStatus("ok");
    } catch (err) {
      setError((err as Error).message);
      setStatus("error");
    }
  }, [consumerId]);

  useEffect(() => {
    void fetchSections();
  }, [fetchSections]);

  return { status, data, error, refresh: fetchSections };
}
